import {
  FindTicketResponse,
  PurchaseOrderRequest,
  PurchaseOldOrderResponse,
  FormValidationResponse,
  HppSessionStatusResponse,
  HttpMessageModel,
  OrderTicketVoidStatusResponse,
  TokenizationInstructionsRequest,
  TokenizationInstructionsResponse,
  DatacapCallbackRequest,
  ContactUsRequest,
  ContactUsResponse,
  ApplyCouponRequest,
  ApplyCouponResponse,
  CalculateRateRequest,
  CalculateRateResponse,
} from '../types/api';
import { getConfig } from './configManager';

/** Shown when card-on-file offers are missing or misconfigured (API returns []). Use HTML for ErrorModal. */
const OFFER_UNAVAILABLE_MESSAGE =
  "We're currently experiencing an issue with offer availability due to a configuration problem.<br /><br />Our team is working on it - please try again shortly.";

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    noResult: boolean = false
  ): Promise<T> {
    const url = `${getConfig().API.BASE_URL}${endpoint}`;

    const request: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'x-site': getConfig().Site,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, request);

      if (!response.ok) {
        if (response.status >= 500) {
          const error = new Error('Something went wrong. Please try again later.');
          (error as any).status = response.status;
          throw error;
        }

        const paylaod: FormValidationResponse = await response.json();
        const error = new Error(paylaod.message || `HTTP error! status: ${response.status}`);
        (error as any).status = response.status;
        (error as any).errors = paylaod.errors;
        throw error;
      }

      // Skip JSON parsing if noResult is true
      if (noResult) {
        return undefined as any;
      }

      return await response.json();

    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Ticket Search APIs
  async findTicketByQR(qrValue: string): Promise<FindTicketResponse> {
    return this.request<FindTicketResponse>(
      `/api/v1/scan2pay/tickets/find-by-qr?Value=${encodeURIComponent(qrValue)}`
    );
  }

  async findTicketByInput(inputValue: string, kind?: string, force?: boolean): Promise<FindTicketResponse> {
    let extraParams = '';
    if (kind) {
      extraParams += `&Kind=${kind}`;
    }
    if (force) {
      extraParams += `&Force=${force}`;
    }
    return this.request<FindTicketResponse>(
      `/api/v1/scan2pay/tickets/find-by-input?Value=${encodeURIComponent(inputValue)}${extraParams}`
    );
  }

  // Order APIs
  async validateOrder(request: PurchaseOrderRequest): Promise<void> {
    await this.request<void>('/api/v1/web/orders/validate', {
      method: 'POST',
      body: JSON.stringify(request),
    }, true); // noResult = true
  }

  async purchaseOrder(request: PurchaseOrderRequest): Promise<PurchaseOldOrderResponse> {
    return this.request<PurchaseOldOrderResponse>('/api/v1/web/orders/purchase', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getOrderVoidStatus(orderId: string): Promise<OrderTicketVoidStatusResponse> {
    return this.request<OrderTicketVoidStatusResponse>(
      `/api/v1/scan2pay/orders/${encodeURIComponent(orderId)}/void-status`,
      { method: 'GET' }
    );
  }

  async getHppStatus(sid: string): Promise<HppSessionStatusResponse> {
    return this.request<HppSessionStatusResponse>(`/api/v1/hpp/${encodeURIComponent(sid)}`, {
      method: 'GET',
    });
  }

  async initHppSession(request?: TokenizationInstructionsRequest): Promise<TokenizationInstructionsResponse> {
    return this.request<TokenizationInstructionsResponse>('/api/v1/hpp/init', {
      method: 'POST',
      ...(request && { body: JSON.stringify(request) }),
    });
  }

  async hppDatacapCallback(request: DatacapCallbackRequest): Promise<void> {
    return this.request<void>('/api/v1/hpp/datacap/on-callback', {
      method: 'POST',
      body: JSON.stringify(request),
    }, true);
  }

  async sendContactUs(request: ContactUsRequest): Promise<ContactUsResponse> {
    const code = encodeURIComponent(getConfig().Site);
    return this.request<ContactUsResponse>(`/api/v1/utils/contact-us/${code}`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Coupon APIs
  async applyCoupon(request: ApplyCouponRequest): Promise<ApplyCouponResponse> {
    return this.request<ApplyCouponResponse>('/api/v1/scan2pay/coupons/apply', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Calculate Rate API
  async calculateRate(request: CalculateRateRequest): Promise<CalculateRateResponse> {
    return this.request<CalculateRateResponse>('/api/v1/scan2pay/tickets/calculate-rate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getOffers(as: string): Promise<any> {
    const offers = await this.request<any>(`/api/v1/web/offers?type=cardonfile&tags=audience:scan-2-pay,continue-as:${as}`, {
      method: 'GET',
    });
    return offers;
  }

  // Helper methods for creating request objects
  async createPurchaseRequest(
    contact: any,
    billingAddress: any,
    isMember: boolean,
    ticketId?: string,
    ticketName?: string,
    generalTerms: boolean = false,
    vehicleNumber?: string | null,
    paymentSid?: string
  ): Promise<PurchaseOrderRequest> {
    const continueAs = isMember ? 'member' : 'guest';
    const offers = await this.getOffers(continueAs);
    const offer = Array.isArray(offers) ? offers[0] : undefined;
    if (!offer?.id) {
      throw new Error(OFFER_UNAVAILABLE_MESSAGE);
    }

    return {
      offerId: offer.id,
      contact,
      billingAddress,
      vehicles: vehicleNumber ? [
        {
          plate: vehicleNumber,
          permit: 'other' as any,
          isDefault: true
        }
      ] : undefined,
      paymentMethod: {
        newCard: {
          sid: paymentSid
        }
      },
      options: {
        openAnAccount: isMember,
        generalTerms: generalTerms,
        marketingTerms: false,
        notifications: isMember ? getConfig().FEATURES.Member.Notifications : getConfig().FEATURES.Guest.Notifications,
        scanToPay: {
          ticketToVoid: ticketId,
          ticketName: ticketName
        }
      }
    };
  }

  // Helper method to update an existing request with a new SID
  updateRequestWithSid(request: PurchaseOrderRequest, sid: string): PurchaseOrderRequest {
    return {
      ...request,
      paymentMethod: {
        newCard: {
          sid: sid
        }
      }
    };
  }
}

export const apiService = new ApiService();
export default apiService; 